import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Clipboard,
  Download,
  Eye,
  File,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFiles, useUploadFile, useDeleteFile } from "@/hooks/useFiles";
import { useToast } from "@/components/ui/use-toast";
import { filesApi } from "@/lib/api";
import {
  formatBytes,
  formatDate,
  isTabularText,
  parseCsv,
  previewKind,
  tsvToCsv,
} from "@/lib/utils";
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from "@/lib/constants";
import type { Attachment } from "@/lib/types";

interface FileAttachmentProps {
  requestId: string;
  canUpload?: boolean;
}

const MAX_PREVIEW_ROWS = 500;

export function FileAttachment({
  requestId,
  canUpload = true,
}: FileAttachmentProps) {
  const { data: files = [], isLoading } = useFiles(requestId);
  const { mutate: uploadFile, isPending } = useUploadFile(requestId);
  const { mutate: deleteFile, isPending: isDeleting } =
    useDeleteFile(requestId);
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Attachment | null>(null);

  // Paste-to-attach: clipboard images become screenshots, copied spreadsheet
  // cells become a CSV. Active only while mounted with canUpload=true.
  useEffect(() => {
    if (!canUpload) return;

    const stamp = () =>
      new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

    const handlePaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      // Screenshots pasted into a comment belong to that comment.
      const pasteTarget = e.target instanceof Element ? e.target : null;
      if (pasteTarget?.closest("[data-comment-composer]")) return;

      const imageItems = Array.from(e.clipboardData.items).filter(
        (item) =>
          item.kind === "file" &&
          (item.type.startsWith("image/") || item.type === ""),
      );

      if (imageItems.length > 0) {
        const files: File[] = imageItems
          .map((item) => {
            const blob = item.getAsFile();
            if (!blob) return null;
            // Normalise to png when type is unknown (some clipboard providers omit it)
            const mimeType = item.type || "image/png";
            const ext = mimeType.split("/")[1] ?? "png";
            // Use window.File to avoid shadowing by @types/node's File declaration
            return new window.File([blob], `screenshot-${stamp()}.${ext}`, {
              type: mimeType,
            });
          })
          .filter((f): f is File => f !== null)
          // Clipboard images are already validated as image/* above — skip the
          // file-picker allowlist so tiff/bmp/webp screenshots are not silently dropped.
          .filter((f) => f.size <= MAX_FILE_SIZE_BYTES);

        if (files.length === 0) return;

        uploadFile(files, {
          onSuccess: (count) =>
            toast({
              title: "Screenshot attached",
              description: `${count} screenshot${count !== 1 ? "s" : ""} uploaded from clipboard`,
            }),
          onError: (err) =>
            toast({
              title: "Paste upload failed",
              description: err.message,
              variant: "destructive",
            }),
        });
        return;
      }

      // A tabbed paste into a text field is a message draft, not an attachment.
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest("textarea, input, [contenteditable='true']")) return;

      const text = e.clipboardData.getData("text/plain");
      if (!isTabularText(text)) return;

      const csv = new window.File(
        [tsvToCsv(text)],
        `pasted-table-${stamp()}.csv`,
        { type: "text/csv" },
      );
      if (csv.size > MAX_FILE_SIZE_BYTES) return;

      uploadFile([csv], {
        onSuccess: () =>
          toast({
            title: "Table attached",
            description: `${csv.name} created from the pasted cells`,
          }),
        onError: (err) =>
          toast({
            title: "Paste upload failed",
            description: err.message,
            variant: "destructive",
          }),
      });
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [canUpload, uploadFile, toast]);

  const handleDelete = (attachmentId: string, filename: string) => {
    deleteFile(attachmentId, {
      onSuccess: () => {
        toast({
          title: "Attachment deleted",
          description: `${filename} has been removed`,
        });
      },
      onError: (err) => {
        toast({
          title: "Delete failed",
          description: err.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Validate all files before uploading
    const filesToUpload: File[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast({
          title: "Unsupported file type",
          description: `${file.name}: Allowed types are PDF, images, Word, Excel, CSV, text`,
          variant: "destructive",
        });
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: "File too large",
          description: `${file.name}: Maximum file size is 25 MB`,
          variant: "destructive",
        });
        continue;
      }

      filesToUpload.push(file);
    }

    if (filesToUpload.length === 0) return;

    uploadFile(filesToUpload, {
      onSuccess: (uploadedCount) => {
        toast({
          title: "Files uploaded successfully",
          description: `${uploadedCount} file${uploadedCount !== 1 ? "s" : ""} uploaded`,
        });
      },
      onError: (err) =>
        toast({
          title: "Upload failed",
          description: err.message,
          variant: "destructive",
        }),
    });

    // Reset input so the same files can be re-selected
    if (inputRef.current) inputRef.current.value = "";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* File list */}
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No attachments</p>
      ) : (
        <TooltipProvider delayDuration={200}>
          <ul className="space-y-2">
            {files.map((file) => (
              <li
                key={file.id}
                className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
              >
                <File className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{file.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(file.size_bytes)} ·{" "}
                    {formatDate(file.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {previewKind(file.content_type) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setPreview(file)}
                        >
                          <Eye className="h-4 w-4" />
                          <span className="sr-only">
                            Preview {file.filename}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Preview</TooltipContent>
                    </Tooltip>
                  )}
                  {file.download_url && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          asChild
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                        >
                          <a
                            href={file.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={file.filename}
                          >
                            <Download className="h-4 w-4" />
                            <span className="sr-only">
                              Download {file.filename}
                            </span>
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Download</TooltipContent>
                    </Tooltip>
                  )}
                  {/* FEATURE: Delete button for requestors to remove wrong attachments */}
                  {canUpload && (
                    <>
                      {(previewKind(file.content_type) ||
                        file.download_url) && (
                        <div className="h-[18px] w-px bg-border" aria-hidden />
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            disabled={isDeleting}
                            onClick={() => handleDelete(file.id, file.filename)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">
                              Delete {file.filename}
                            </span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </TooltipProvider>
      )}

      {/* Upload */}
      {canUpload && (
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ALLOWED_FILE_TYPES.join(",")}
            onChange={handleFileChange}
            className="sr-only"
            id="file-upload"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={isPending}
            onClick={() => inputRef.current?.click()}
            className="gap-2"
          >
            {isPending ? (
              <>
                <Upload className="h-4 w-4 animate-bounce" />
                Uploading…
              </>
            ) : (
              <>
                <Paperclip className="h-4 w-4" />
                Attach files
              </>
            )}
          </Button>
          <p className="mt-1 text-xs text-muted-foreground">
            Select multiple files · PDF, images, Word, Excel, CSV · Max 25 MB
            each
          </p>
          <p className="mt-0.5 flex items-center flex-wrap gap-x-1 gap-y-0.5 text-xs text-muted-foreground/70">
            <Clipboard className="h-3 w-3 shrink-0" />
            Copy an image or spreadsheet cells, then paste with{" "}
            <kbd className="rounded border px-1 font-mono text-[10px]">
              Ctrl+V
            </kbd>{" "}
            /{" "}
            <kbd className="rounded border px-1 font-mono text-[10px]">⌘V</kbd>
            <span className="text-muted-foreground/50">
              · Mac:{" "}
              <kbd className="rounded border px-1 font-mono text-[10px]">
                ⌘⌃⇧4
              </kbd>{" "}
              · Win:{" "}
              <kbd className="rounded border px-1 font-mono text-[10px]">
                ⊞+Shift+S
              </kbd>
            </span>
          </p>
        </div>
      )}

      <AttachmentPreview
        requestId={requestId}
        file={preview}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}

function AttachmentPreview({
  requestId,
  file,
  onClose,
}: {
  requestId: string;
  file: Attachment | null;
  onClose: () => void;
}) {
  const kind = file ? previewKind(file.content_type) : null;

  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        {file && (
          <>
            <DialogHeader>
              <DialogTitle className="truncate pr-6">
                {file.filename}
              </DialogTitle>
              <DialogDescription>
                {formatBytes(file.size_bytes)} · {file.content_type}
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[70vh] overflow-auto rounded-md border">
              {kind === "image" && file.download_url && (
                <img
                  src={file.download_url}
                  alt={file.filename}
                  className="mx-auto max-h-[70vh] object-contain"
                />
              )}
              {kind === "pdf" && file.download_url && (
                <iframe
                  src={file.download_url}
                  title={file.filename}
                  className="h-[70vh] w-full"
                />
              )}
              {kind === "text" && (
                <TextPreview requestId={requestId} file={file} />
              )}
              {kind !== "text" && !file.download_url && (
                <p className="p-6 text-sm text-muted-foreground">
                  Preview is unavailable for this file.
                </p>
              )}
            </div>

            {file.download_url && (
              <DialogFooter>
                <Button asChild variant="outline" size="sm">
                  <a
                    href={file.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={file.filename}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </a>
                </Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TextPreview({
  requestId,
  file,
}: {
  requestId: string;
  file: Attachment;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["file-content", requestId, file.id],
    queryFn: () => filesApi.content(requestId, file.id),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isError || data === undefined) {
    return (
      <p className="p-6 text-sm text-destructive">Could not load this file.</p>
    );
  }

  if (file.content_type !== "text/csv") {
    return (
      <pre className="whitespace-pre-wrap break-words p-4 text-xs">{data}</pre>
    );
  }

  const rows = parseCsv(data);
  if (rows.length === 0) {
    return (
      <p className="p-6 text-sm text-muted-foreground">This file is empty.</p>
    );
  }

  const [header, ...body] = rows;
  const shown = body.slice(0, MAX_PREVIEW_ROWS);

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            {header.map((h, i) => (
              <TableHead key={i} className="whitespace-nowrap">
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((r, ri) => (
            <TableRow key={ri}>
              {header.map((_, ci) => (
                <TableCell key={ci} className="whitespace-nowrap text-xs">
                  {r[ci] ?? ""}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {body.length > MAX_PREVIEW_ROWS && (
        <p className="border-t px-4 py-2 text-xs text-muted-foreground">
          Showing first {MAX_PREVIEW_ROWS} of {body.length} rows — download for
          the full file.
        </p>
      )}
    </>
  );
}
